import { GlobalProfiles, ProfileConflicts } from "@proof/authoring";
import {
  distinctProfiles,
  profileSelectors,
  type Project,
} from "@proof/core";

/**
 * A project's profiles: which global profile types and reusable forms it uses,
 * and where each profile switcher appears on the form. The first profile
 * pre-fills the form; people switch with the dropdown.
 */
export function ProjectProfiles({
  project,
  onChange,
  notify,
  canAssign,
}: {
  project: Project;
  onChange(project: Project): void;
  notify(message: string): void;
  canAssign: boolean;
}) {
  const profiles = distinctProfiles(project);
  const selectors = profileSelectors(project);
  const where = (key: string) => {
    const places = [
      ...selectors.top
        .filter((selector) => selector.profileKey === key)
        .map(() => "Top of the form"),
      ...project.groups
        .filter((group) => selectors.sections.get(group.id)?.profileKey === key)
        .map((group) => `Section · ${group.name || "Untitled section"}`),
    ];
    return places.length ? places.join(", ") : "Not on the form";
  };
  return (
    <div className="project-profiles">
      <section className="panel padded" aria-label="Profile placement">
        <h2>Profiles on this form</h2>
        <p className="muted">
          With one global profile, a single selector at the top fills every
          linked field. With several, each section holding a profile's
          variables gets its own selector for that profile, and sections choose
          independently. A section cannot mix variables from different
          profiles.
        </p>
        <ProfileConflicts project={project} />
        {!profiles.length && (
          <p>
            No profiles are mapped yet, so the form shows no profile selector.
            Assign a global profile type below, or add a reusable form in Forms.
          </p>
        )}
        {profiles.map((profile) => (
          <div className="profile-placement-row" key={profile.key}>
            <div>
              <strong>{profile.name}</strong>
              <small>
                {profile.fieldIds.size} mapped variables · Shows in{" "}
                {where(profile.key)}
              </small>
            </div>
          </div>
        ))}
      </section>
      <GlobalProfiles
        project={project}
        onChange={onChange}
        notify={notify}
        canAssign={canAssign}
      />
    </div>
  );
}
